export type SubscriptionType = 'PAID' | 'FREE';

export type UserSubscriptionStatus =
	| 'active'
	| 'scheduled'
	| 'expired'
	| 'cancelled'
	| 'banned';

export type DealStatus = 'active' | 'finished' | 'cancelled';

export interface ShopStats {
	products_total: number;
	products_active: number;
	deals_total: number;
	deals_finished: number;
	/** Сумма закрытых сделок по ценам на момент продажи. */
	sold_cents: number;
}

export interface ShopOwner {
	uuid: string;
	telegram_id: number;
	active: boolean;
}

export interface ShopPlan {
	uuid: string;
	name: string;
	type: SubscriptionType;
	price_cents: number;
	duration_days: number;
}

export interface ShopSubscription {
	uuid: string;
	start_date: string;
	end_date: string;
	auto_renewal: boolean;
	status: UserSubscriptionStatus;
	subscription: ShopPlan | null;
}

export interface ShopTheme {
	uuid: string;
	name: string;
	is_global: boolean;
}

export interface ShopProduct {
	uuid: string;
	name_product: string;
	price_cents: number;
	available_quantity: number;
	is_quantifiable: boolean;
	is_active: boolean;
	is_auto_delivery: boolean;
	created_at: string | null;
}

export interface Shop {
	uuid: string;
	name: string;
	bot_name: string;
	description: string;
	image_url: string | null;
	owner_uuid: string;
	category_id: number | null;
	support_group_id: number | null;
	created_at: string | null;

	/** Продавец выключил магазин сам — или владельца забанили. */
	active: boolean;
	/** Telegram ответил 401: бота удалили или отозвали токен. */
	bot_alive: boolean;
	/** Создание магазина не довели до конца. */
	is_onboarding: boolean;

	owner: ShopOwner | null;
	subscription: ShopSubscription | null;
	/** Оплаченный вперёд период: куплен, начнётся когда кончится текущий. */
	next_subscription: ShopSubscription | null;
	theme: ShopTheme | null;
	stats: ShopStats;
	/** Только в карточке: в списке товары не приходят. */
	products: ShopProduct[] | null;
}

export interface Shops {
	data: Shop[];
	total: number;
	offset: number;
	limit: number;
}

export interface ShopDealPurchase {
	uuid: string;
	product_item_uuid: string;
	buyer_uuid: string;
	status: string;
	deal_bid_uuid: string;
	/** Цена на момент продажи, а не текущая цена товара. */
	price_cents: number;
}

export interface ShopDealProduct {
	uuid: string;
	shop_uuid: string;
	name_product: string;
	image_url: string | null;
	price_cents: number;
	is_quantifiable: boolean;
	is_auto_delivery: boolean;
	sold_quantity: number;
	purchases: ShopDealPurchase[] | null;
}

export interface ShopDeal {
	uuid: string;
	human_id: string;
	deal_uuid: string;
	status: DealStatus;
	products: ShopDealProduct[] | null;
}

export interface ShopDeals {
	data: ShopDeal[];
	total: number;
	limit: number | null;
	offset: number | null;
}

/**
 * `inactive` — магазин выключен решением продавца или баном.
 * `problem` — поломка, о которой продавец может не знать: Telegram отозвал
 * бота, оформление брошено, категория не выбрана. Такой магазин числится
 * включённым, и по флагу «вкл/выкл» его не найти.
 */
export type ShopStateFilter = 'active' | 'inactive' | 'problem';

export interface ShopsFilter {
	name: string;
	state: ShopStateFilter | null;
	page: number;
}

export interface SubscriptionHistoryItem {
	uuid: string;
	user_uuid: string;
	subscription_uuid: string;
	start_date: string;
	end_date: string;
	auto_renewal: boolean;
	status: UserSubscriptionStatus;
	subscription: ShopPlan | null;
	/** Магазин, к которому период был привязан. */
	shop: { uuid: string; name: string } | null;
}

export interface SubscriptionHistory {
	data: SubscriptionHistoryItem[];
	total: number;
	offset: number | null;
	limit: number | null;
}

export interface UserTheme {
	uuid: string;
	name: string;
	description: string;
	is_global: boolean;
	user_uuid: string | null;
	background: string;
	surface: string;
	primary: string;
	text: string;
	shops: { uuid: string; name: string }[] | null;
}

export interface UserThemes {
	data: UserTheme[];
	total: number;
}

/**
 * Почему магазин не торгует — одной строкой.
 *
 * Причин три, и они не взаимозаменяемы: выключенный продавцом магазин
 * чинится им самим, мёртвый бот — заменой токена, недооформленный — тем,
 * что продавца надо довести до конца создания. Первая подходящая и есть
 * та, с которой оператору идти к продавцу.
 */
export const shopState = (
	shop: Shop
): { label: string; badge: string } => {
	if (!shop.active) return { label: 'Выключен', badge: 'badge-light-danger' };
	if (!shop.bot_alive)
		return { label: 'Бот мёртв', badge: 'badge-light-danger' };
	if (shop.is_onboarding)
		return { label: 'Не дооформлен', badge: 'badge-light-warning' };
	if (shop.category_id === null)
		return { label: 'Без категории', badge: 'badge-light-warning' };
	return { label: 'Работает', badge: 'badge-light-success' };
};

export const SUBSCRIPTION_STATUS_LABELS: Record<UserSubscriptionStatus, string> =
	{
		active: 'Активна',
		scheduled: 'Оплачена вперёд',
		expired: 'Истекла',
		cancelled: 'Отменена',
		banned: 'Заблокирована',
	};

export const DEAL_STATUS: Record<DealStatus, { label: string; badge: string }> =
	{
		active: { label: 'В работе', badge: 'badge-light-primary' },
		finished: { label: 'Завершена', badge: 'badge-light-success' },
		cancelled: { label: 'Отменена', badge: 'badge-light-danger' },
	};

export const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export const formatDate = (value: string | null | undefined) =>
	value ? new Date(value).toLocaleDateString('ru-RU') : '—';

export const formatDateTime = (value: string | null | undefined) =>
	value ? new Date(value).toLocaleString('ru-RU') : '—';
